import type { AutofixInpaintCheckoutRequest, AutofixOutpaintCheckoutRequest, AutofixRecolorCheckoutRequest, AutoFixRequest } from '@/types/autoFixRequest'
import type { CreativeResponse } from '@/types/creativeResponse'
import CreativeService from '@/services/creativeService'
import { getActivePinia } from 'pinia'
import { useBrandStore } from '@/stores/brand'
import { usePlacementStore } from '@/stores/placement'
import { useCanvasStore } from '@/stores/canvas'
import { useUploadedCreativeStore } from '@/stores/uploadedCreative'
import { useEnhancementsStore } from '@/stores/enhancement'
import { useProductStore } from '@/stores/product'
import { standardFontFamilies } from '@/constants/fonts'
import WebFont from 'webfontloader'
import { discountTypes, discountVariations, squareAspectRatioBuffer } from '@/constants/canvas'
import type { Validation } from '@/types/validation'
import type { CreativeSetGuidelines } from '@/types/creativeSetGuidelines'
import { PLACEMENT_TYPES, PRODUCT_ASPECT_TYPES } from '@/constants/positions'
import { classifyAspectRatio } from './productPlacementHelper'
import { type Canvas, type FabricObject } from 'fabric'
import type { Creative } from '@/types/creative'
import { useCreativeStore } from '@/stores/creative'
import { useEditorStore } from '@/stores/editor'
import { getImageAspectRatio } from '@/helpers/logoHelper'
import { capitalize, getContrastColor } from './commonHelper'
import ProductsNotPresentException from '@/errors/ProductsNotPresentException'
import { AI_BACKGROUND_TEXT_COLOR, BACKDROP_TEXT_COLORS } from './backgroundHelper'
import type { OutpaintRequest } from '@/types/AutoFixResponse'
import { useAutofixStore } from '@/stores/autofix'
import { useAssetStore } from '@/stores/asset'
import { useAppStore } from '@/stores/app'
import designFlows from '@/constants/designFlows'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'
import features from '@/constants/features'

// Mutable copy for dynamic adjustment - initialized with defaults
const VALID_PLACEMENT_AREA = { left: 458, top: 80, right: 660, bottom: 400 }

export async function autoFixCreative(signal: AbortSignal): Promise<void> {
  if (!getActivePinia()) {
    throw new Error('Pinia is not initialized. Make sure app.use(pinia) is called.')
  }
  const brandStore = useBrandStore()
  const canvasStore = useCanvasStore()
  const placementStore = usePlacementStore()
  const uploadedCreativeStore = useUploadedCreativeStore()
  const enhancementStore = useEnhancementsStore()
  const productStore = useProductStore()
  const editorStore = useEditorStore()
  const autofixStore = useAutofixStore()
  const assetStore = useAssetStore()
  const creativeService = new CreativeService()
  if (editorStore.isAutoFix) {
    brandStore.selectedLogo = brandStore.baseLogo
    canvasStore.discountType = canvasStore.baseDiscountInfo.type
    canvasStore.discountValue = canvasStore.baseDiscountInfo.value
    canvasStore.discountVariation = canvasStore.baseDiscountInfo.variation
    canvasStore.rawCouponCode = canvasStore.baseDiscountInfo.couponCode
    placementStore.selectedBackgroundImage = placementStore.baseBackgroundImage
    placementStore.selectedAIBackgroundImage = ''
    const discountInfo = {
      type: canvasStore.discountType,
      value: canvasStore.discountValue,
      couponCode: canvasStore.rawCouponCode,
      variation: canvasStore.discountVariation,
    }
    canvasStore.setLastUsedDiscountInfo(discountInfo)
    return
  }
  try {
    let responseData = autofixStore.detectionsResponse
    if (responseData === null) {
      const requestData: AutoFixRequest = {
        brandName: brandStore.selectedBrand?.name || '',
        logoUrl: brandStore.selectedLogo?.logoUrl || '',
        adImageUrl: uploadedCreativeStore.uploadedCreativeUrl,
        canvasJson: JSON.stringify(placementStore.selectedPublishedVariant?.canvasJson),
        guidelinesData: JSON.stringify(placementStore.selectedPlacement?.creativeSetGuidelines),
        baseBgImageUrl: placementStore.selectedBackgroundImage || '',
        primaryColor: brandStore.selectedColor || null,
        // Discount is read from the ad, so the form's discount is not sent.
        discountInfo: JSON.stringify({
          isEnabled: false,
          type: discountTypes.none,
          variation: discountVariations.upto,
          value: '',
          couponCode: '',
        }),
        featureId: features.AUTOFIX_CREATIVE
      }
      const result = await creativeService.handleDetections(requestData, signal)
      if (result.status === 204) {
        throw new ProductsNotPresentException(result.message || "No products present for autofix.");
      }
      if (result.status === 500 || result.data === undefined) {
        throw new Error(result.message);
      }
      responseData = result.data
      autofixStore.detectionsResponse = responseData
    }

    let inpaintingResponse = autofixStore.inpaintingResponse
    // Get placement area from config - use maximum placement area for autofix
    const adSizeStore = useAdSizeConfigStore()
    const maxPlacement = adSizeStore.getMaximumPlacementArea()
    const canvasSize = adSizeStore.canvasSize
    const placementArea = {
      left: maxPlacement.left + 20, // Add small padding
      top: maxPlacement.top,
      right: maxPlacement.right,
      bottom: canvasSize.height + 1
    }
    if (inpaintingResponse === null) {
      const inpaintingRequestData: AutofixInpaintCheckoutRequest = {
        guidelinesData: JSON.stringify(placementStore.selectedPlacement?.creativeSetGuidelines),
        placementArea: JSON.stringify(placementArea),
        originalImageUrl: responseData.originalImageUrl,
        maskUrl: responseData.maskUrl,
        combinedProductBox: responseData.combinedProductBox,
        adId: responseData.adId,
        combinedMaskProductUrl: responseData.combinedMaskProductUrl,
        inpaintingRequired: responseData.inpaintingRequired,
        featureId: features.AUTOFIX_CREATIVE
      }
      const inpaintingResult = await creativeService.handleInpaint(inpaintingRequestData, signal)
      if (inpaintingResult.status === 500 || inpaintingResult.data === undefined) {
        throw new Error(`Unexpected status: ${inpaintingResult.status}`);
      }

      inpaintingResponse = inpaintingResult.data
      autofixStore.inpaintingResponse = inpaintingResponse
    }

    let originalUrl: string | null = inpaintingResponse.originalUrl
    if (inpaintingResponse.outpaintingNeeded) {
      originalUrl = autofixStore.outpaintingResponse
      if (originalUrl === null) {
        const outpaintingRequestData: OutpaintRequest = {
          ...inpaintingResponse.outpaintRequest,
          featureId: features.AUTOFIX_CREATIVE
        }
        const outpaintingResult = await creativeService.handleOutpaint(outpaintingRequestData, signal)
        if (outpaintingResult.status === 500 || outpaintingResult.data === undefined) {
          throw new Error(`Unexpected status: ${outpaintingResult.status}`);
        }
        if (outpaintingResult.status === 500 || outpaintingResult.data === undefined) {
          throw new Error(outpaintingResult.message);
        }
        originalUrl = outpaintingResult.data
        autofixStore.outpaintingResponse = originalUrl
      }
    }

    let finalData = autofixStore.recoloringResponse
    if (finalData === null) {
      const appStore = useAppStore()
      const recoloringRequest: AutofixRecolorCheckoutRequest = {
        guidelinesData: JSON.stringify(placementStore.selectedPlacement?.creativeSetGuidelines),
        adUrl: originalUrl,
        adId: responseData.adId,
        backgroundType: responseData.backgroundType,
        originalImageUrl: uploadedCreativeStore.uploadedCreativeUrl,
        designType: appStore.selectedDesignFlow.value,
        featureId: features.AUTOFIX_CREATIVE
      }

      const recoloringResult = await creativeService.handleRecolor(recoloringRequest, signal)
      if (recoloringResult.status === 500 || recoloringResult.data === undefined) {
        throw new Error(`Unexpected status: ${recoloringResult.status}`);
      }
      if (recoloringResult.status === 500 || recoloringResult.data === undefined) {
        throw new Error(recoloringResult.message);
      }
      finalData = recoloringResult.data
      autofixStore.recoloringResponse = finalData
    }

    if (autofixStore.isAutofixHappening == false) {
      return
    }

    let isRectangleLogo = false
    const logoAspectRatio = await getImageAspectRatio(brandStore.selectedLogo?.logoUrl || '')
    if (!autofixStore.isAutofixHappening) return
    if (logoAspectRatio >= squareAspectRatioBuffer) {
      isRectangleLogo = true
    }
    const textColor = finalData.textColor
    canvasStore.textColor = textColor
    editorStore.contrastRatio = finalData.contrastRatio
    if (!autofixStore.isAutofixHappening) return
    const canvasJson = JSON.parse(
      JSON.stringify(placementStore.selectedPublishedVariant?.canvasJson)
    );
    for (const object of canvasJson.objects) {
      if (!autofixStore.isAutofixHappening) return
      switch (object.layerName) {
        case 'Product1':
          object.left = inpaintingResponse.productLeftOnCanvas;
          object.visible = false;
          break;

        case 'Headline':
          object.text = capitalize(responseData.headline);
          object.fill = textColor;
          break;

        case 'SubHeadline':
          object.text = capitalize(responseData.subheadline);
          object.fill = textColor;
          break;

        case 'CTA':
          object.visible = responseData.cta.toUpperCase().includes('BUY NOW')
          const textbox = object.objects?.find((obj: any) => obj.type === 'Textbox');
          if (textbox) {
            textbox.fill = textColor === '#000000'
              ? textColor
              : responseData.primaryColor;
          }
          break;

        case 'CTA - Order now':
          object.visible = responseData.cta.toUpperCase().includes('ORDER NOW')
          const textBox = object.objects?.find((obj: any) => obj.type === 'Textbox');
          if (textBox) {
            textBox.fill = textColor === '#000000'
              ? textColor
              : responseData.primaryColor;
          }
          break;

        case 'Logo':
          if (isRectangleLogo) {
            object.visible = false
            break
          }
          object.src = brandStore.selectedLogo?.logoUrl;
          // Create a block scope to contain these variables
          const logoResponse = await fetch(brandStore.selectedLogo?.logoUrl || '');
          const logoBlob = await logoResponse.blob();
          const logoImageBitmap = await createImageBitmap(logoBlob);

          const logoImageWidth = logoImageBitmap.width;
          const logoImageHeight = logoImageBitmap.height;
          // Calculate scale factors

          // Preserve aspect ratio by taking the smaller scale
          object.width = logoImageWidth
          object.height = logoImageHeight
          object.scaleX = 1
          object.scaleY = 1
          break;

        case 'Logo - Horizontal':
          if (!isRectangleLogo) {
            object.visible = false
          }
          object.src = brandStore.selectedLogo?.logoUrl;
          // Create a separate block scope for these variables
          const response = await fetch(brandStore.selectedLogo?.logoUrl || '');
          const blob = await response.blob();
          const imageBitmap = await createImageBitmap(blob);

          const horizontalLogoImageWidth = imageBitmap.width;
          const horizontalLogoImageHeight = imageBitmap.height;
          // Calculate scale factors

          // Preserve aspect ratio by taking the smaller scale
          object.width = horizontalLogoImageWidth
          object.height = horizontalLogoImageHeight
          object.scaleX = 1
          object.scaleY = 1
          break;
      }
    }

    if (!autofixStore.isAutofixHappening) return

    const uniqueFonts: string[] = [
      ...new Set<string>(
        canvasJson.objects
          .filter(
            (obj: any) =>
              (obj.layerType === 'Text' || obj.type === 'Textbox') &&
              typeof obj.fontFamily === 'string',
          )
          .map((obj: { fontFamily: string }) => obj.fontFamily)
          .filter((font: any) => !standardFontFamilies.includes(font)),
      ),
    ]

    if (!autofixStore.isAutofixHappening) return

    if (uniqueFonts.length) {
      await new Promise<void>((resolve) => {
        WebFont.load({
          google: { families: uniqueFonts },
          active: () => {
            canvasStore.setCanvasJson(canvasJson)
            resolve()
          },
          inactive: () => {
            console.warn('Some fonts failed to load. Falling back to default font.')
            canvasStore.setCanvasJson(canvasJson)
            resolve()
          },
        })
      })
    } else {
      canvasStore.setCanvasJson(canvasJson)
    }

    if (!autofixStore.isAutofixHappening) return

    brandStore.setSelectedColor(responseData.primaryColor)
    brandStore.setBaseData(
      brandStore.selectedLogo,
      finalData.backgroundColor,
      [],
    )
    placementStore.setSelectedBackgroundImage(finalData.backgroundUrl)
    placementStore.setSelectedAIBackgroundImage('')
    placementStore.baseBackgroundImage = finalData.backgroundUrl
    placementStore.baseAIBackgroundImage = ''
    productStore.setSelectedProducts([])
    // Start from no discount so only what was detected in the ad is kept.
    canvasStore.resetDiscountInfo()
    if (!responseData.discountType.toLowerCase().startsWith("none")) {
      if (responseData.discountType.toLowerCase().startsWith("percentage")) {
        canvasStore.discountType = discountTypes.percentage
      }
      else if (responseData.discountType.toLowerCase().startsWith("fixed")) {
        canvasStore.discountType = discountTypes.fixed
      }
      canvasStore.discountValue = responseData.discountValue
      if (responseData.discountVariation.toLowerCase().startsWith("upto")) {
        canvasStore.discountVariation = discountVariations.upto
      }
      else if (responseData.discountVariation.toLowerCase().startsWith("flat")) {
        canvasStore.discountVariation = discountVariations.flat
      }

      if (!responseData.couponCode.toLowerCase().startsWith("n/a")) {
        canvasStore.rawCouponCode = responseData.couponCode.toUpperCase()
      }
    }

    const discountInfo = {
      type: canvasStore.discountType,
      value: canvasStore.discountValue,
      couponCode: canvasStore.rawCouponCode,
      variation: canvasStore.discountVariation,
    }
    canvasStore.setBaseDiscountInfo(discountInfo)
    canvasStore.setLastUsedDiscountInfo(discountInfo)
    editorStore.isAutoFix = true
    editorStore.autofixBackgroundType = finalData.backgroundType
  }
  catch (error) {
    throw error;
  }
}

export const findCreativeInTree = (creatives: Creative[], id: string): Creative | null => {
  for (const creative of creatives) {
    if (creative.id === id) return creative
    if (creative.subCreatives) {
      const found = findCreativeInTree(creative.subCreatives, id)
      if (found) return found
    }
  }
  return null
}

interface Position {
  top: number
  left: number
  width: number
  height: number
}

interface Layer {
  id: number
  name: string
  type: string
  sub_type: string
  value: string
  position: Position
  originX: string
  originY: string
  scaleX: number
  scaleY: number
  angle: number
  styles: { [key: string]: any }
  is_product: boolean
  visible: boolean
}

interface LayerMapping {
  [key: string]: {
    type: string
    sub_type: string
    is_product: boolean
  }
}

interface CanvasLayers {
  name: string
  width: number
  height: number
  objects: Layer[]
}

interface AdSize {
  width: number
  height: number
}

const adjustPositionForOrigin = (
  top: number,
  left: number,
  height: number,
  width: number,
  originY: string = 'top',
  originX: string = 'left',
  scaleX: number = 1,
  scaleY: number = 1,
): { top: number; left: number } => {
  const scaledHeight = height * scaleY
  const scaledWidth = width * scaleX

  let adjustedTop = top
  let adjustedLeft = left

  if (originY === 'center') {
    adjustedTop = top - scaledHeight / 2
  } else if (originY === 'bottom') {
    adjustedTop = top - scaledHeight
  }

  if (originX === 'center') {
    adjustedLeft = left - scaledWidth / 2
  } else if (originX === 'right') {
    adjustedLeft = left - scaledWidth
  }

  return { top: adjustedTop, left: adjustedLeft }
}

const convertCanvasToLayers = (
  canvasData: any,
  adSize: AdSize,
  name: string = 'Red Bull',
  layerMapping: LayerMapping | null = null,
): CanvasLayers => {
  // Default layer mapping
  const defaultLayerMapping: LayerMapping = {
    Logo: { type: 'image', sub_type: 'logo', is_product: false },
    'Logo - Horizontal': { type: 'image', sub_type: 'logo', is_product: false },
    Product1: { type: 'image', sub_type: 'product', is_product: true },
    Headline: { type: 'text', sub_type: 'headline', is_product: false },
    SubHeadline: { type: 'text', sub_type: 'subheadline', is_product: false },
    CTA: { type: 'button', sub_type: 'CTA', is_product: false },
    'CTA - Order now': { type: 'button', sub_type: 'CTA', is_product: false },
    BG: { type: 'image', sub_type: 'background', is_product: false },
  }

  const mapping = layerMapping || defaultLayerMapping

  // Extract dimensions
  const width = adSize.width
  const height = adSize.height

  // Initialize output structure
  const output: CanvasLayers = {
    name,
    width,
    height,
    objects: [],
  }

  // Map objects from input to output format
  let objectId = 1
  const backgroundUrl = canvasData.backgroundImage?.src || ''

  for (const obj of canvasData.objects) {
    const layerName = obj.layerName || ''

    // Skip if layer is not in mapping
    if (!(layerName in mapping)) {
      continue
    }

    const mapInfo = mapping[layerName]

    const { top: adjustedTop, left: adjustedLeft } = adjustPositionForOrigin(
      obj.top ?? 0,
      obj.left ?? 0,
      obj.height ?? 0,
      obj.width ?? 0,
      obj.originY ?? 'top',
      obj.originX ?? 'left',
      obj.scaleX ?? 1,
      obj.scaleY ?? 1,
    )

    const scaledWidth = (obj.width ?? 0) * (obj.scaleX ?? 1)
    const scaledHeight = (obj.height ?? 0) * (obj.scaleY ?? 1)

    // Check if object is completely outside canvas
    const isOutsideCanvas =
      adjustedLeft + scaledWidth < 0 ||
      adjustedLeft > adSize.width ||
      adjustedTop + scaledHeight < 0 ||
      adjustedTop > adSize.height

    if (isOutsideCanvas) {
      continue
    }

    // Create basic object structure
    const formattedLayerName = layerName.replace(/^Product(\d+)$/, 'Product $1')
    const newObj: Layer = {
      id: objectId,
      name: formattedLayerName,
      type: mapInfo.type,
      sub_type: mapInfo.sub_type,
      value: '',
      position: {
        top: obj.top ?? 0,
        left: obj.left ?? 0,
        width: obj.width ?? 0,
        height: obj.height ?? 0,
      },
      originX: obj.originX ?? 'left',
      originY: obj.originY ?? 'top',
      scaleX: obj.scaleX ?? 1,
      scaleY: obj.scaleY ?? 1,
      angle: obj.angle ?? 0,
      styles: {},
      is_product: mapInfo.is_product,
      visible: obj.visible,
    }

    // Handle type-specific properties
    if (mapInfo.type === 'image') {
      newObj.value = obj.src || ''
    } else if (mapInfo.type === 'text') {
      newObj.value = obj.text || ''
      newObj.styles.fill = obj.fill || '#FFFFFF'
    } else if (mapInfo.type === 'button') {
      let buttonText = ''
      let buttonTextFill = '#000000'

      if (obj.objects) {
        for (const item of obj.objects) {
          if (item.type === 'Textbox') {
            buttonText = item.text || ''
            buttonTextFill = item.fill || '#000000'
          }
        }
      }

      newObj.value = buttonText
      newObj.styles.fill = buttonTextFill
    }

    output.objects.push(newObj)
    objectId++
  }
  for (const obj of canvasData.objects) {
    if (obj.type === 'Group') {
      for (const item of obj.objects) {
        if (item.type === 'Image') {
          const newProductObj: Layer = {
            id: objectId,
            name: 'Product 1',
            type: 'image',
            sub_type: 'product',
            value: item.src || '',
            position: {
              top: obj.top || 0,
              left: obj.left || 0,
              width: obj.width || 0,
              height: obj.height || 0,
            },
            originX: obj.originX || 'left',
            originY: obj.originY || 'top',
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
            angle: obj.angle || 0,
            styles: {},
            is_product: true,
            visible: obj.visible
          }
          output.objects.push(newProductObj)
          break
        }
      }
    }
  }

  // Add background if not already present
  if (!output.objects.some((obj) => obj.name === 'BG')) {
    output.objects.push({
      id: objectId,
      name: 'BG',
      type: 'image',
      sub_type: 'background',
      value: backgroundUrl,
      position: { top: 0, left: 0, width, height },
      originX: 'left',
      originY: 'top',
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      styles: {},
      is_product: false,
      visible: true
    })
  }

  return output
}

enum EdgeType {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

const clipAgainstAxisAlignedEdge = (
  polygon: { x: number; y: number }[],
  edgeType: EdgeType,
  edgeValue: number,
): { x: number; y: number }[] => {
  if (polygon.length === 0) return []

  const output: { x: number; y: number }[] = []
  let prev = polygon[polygon.length - 1]
  let prevInside = isInsideAxisAligned(prev, edgeType, edgeValue)

  for (const current of polygon) {
    const currentInside = isInsideAxisAligned(current, edgeType, edgeValue)

    if (currentInside) {
      if (!prevInside) {
        output.push(intersectAxisAligned(prev, current, edgeType, edgeValue))
      }
      output.push(current)
    } else if (prevInside) {
      output.push(intersectAxisAligned(prev, current, edgeType, edgeValue))
    }

    prev = current
    prevInside = currentInside
  }

  return output
}

const isInsideAxisAligned = (
  point: { x: number; y: number },
  edgeType: EdgeType,
  edgeValue: number,
): boolean => {
  switch (edgeType) {
    case EdgeType.LEFT:
      return point.x >= edgeValue
    case EdgeType.RIGHT:
      return point.x <= edgeValue
    case EdgeType.TOP:
      return point.y >= edgeValue
    case EdgeType.BOTTOM:
      return point.y <= edgeValue
  }
}

const intersectAxisAligned = (
  A: { x: number; y: number },
  B: { x: number; y: number },
  edgeType: EdgeType,
  edgeValue: number,
): { x: number; y: number } => {
  if (edgeType === EdgeType.LEFT || edgeType === EdgeType.RIGHT) {
    const t = (edgeValue - A.x) / (B.x - A.x)
    return {
      x: edgeValue,
      y: A.y + t * (B.y - A.y),
    }
  } else {
    const t = (edgeValue - A.y) / (B.y - A.y)
    return {
      x: A.x + t * (B.x - A.x),
      y: edgeValue,
    }
  }
}

const clipPolygonToCanvas = (
  polygon: { x: number; y: number }[],
  canvasWidth: number,
  canvasHeight: number,
): { x: number; y: number }[] => {
  let result = polygon
  result = clipAgainstAxisAlignedEdge(result, EdgeType.LEFT, 0)
  result = clipAgainstAxisAlignedEdge(result, EdgeType.RIGHT, canvasWidth)
  result = clipAgainstAxisAlignedEdge(result, EdgeType.TOP, 0)
  result = clipAgainstAxisAlignedEdge(result, EdgeType.BOTTOM, canvasHeight)
  return result
}

const polygonArea = (vertices: { x: number; y: number }[]): number => {
  let area = 0
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length
    area += vertices[i].x * vertices[j].y
    area -= vertices[i].y * vertices[j].x
  }
  return Math.abs(area) / 2
}

const getRotatedCorners = (layer: Layer): { x: number; y: number }[] => {
  const { position, originX, originY, scaleX, scaleY, angle } = layer
  const width = position.width * scaleX
  const height = position.height * scaleY

  const offsetX = originX === 'left' ? 0 : originX === 'center' ? width / 2 : width
  const offsetY = originY === 'top' ? 0 : originY === 'center' ? height / 2 : height

  const corners = [
    { x: -offsetX, y: -offsetY },
    { x: width - offsetX, y: -offsetY },
    { x: width - offsetX, y: height - offsetY },
    { x: -offsetX, y: height - offsetY },
  ]

  const radians = (angle * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)

  return corners.map((corner) => ({
    x: corner.x * cos - corner.y * sin + position.left,
    y: corner.x * sin + corner.y * cos + position.top,
  }))
}

// Helper to compute the center of a rotated rectangle
const getRotatedCenter = (layer: Layer): { x: number; y: number } => {
  const corners = getRotatedCorners(layer)
  const center = corners.reduce(
    (acc, corner) => ({ x: acc.x + corner.x / 4, y: acc.y + corner.y / 4 }),
    { x: 0, y: 0 },
  )
  return center
}

const validatePositionsWithCanvasJson = (
  layers: Layer[],
  newLayers: Layer[],
  adSize: AdSize,
): Validation[] => {
  const smallBuffer = 10
  const largeBuffer = 30

  const backgroundLayer = newLayers.find((layer) => layer.sub_type === 'background')
  const canvasWidth =
    backgroundLayer &&
      backgroundLayer.position &&
      typeof backgroundLayer.position.width === 'number' &&
      typeof backgroundLayer.scaleX === 'number'
      ? backgroundLayer.position.width * backgroundLayer.scaleX
      : adSize.width
  const canvasHeight =
    backgroundLayer &&
      backgroundLayer.position &&
      typeof backgroundLayer.position.height === 'number' &&
      typeof backgroundLayer.scaleY === 'number'
      ? backgroundLayer.position.height * backgroundLayer.scaleY
      : adSize.height

  const originalByType: { [key: string]: Layer[] } = {}
  for (const layer of layers) {
    const layerType = layer.sub_type
    originalByType[layerType] = originalByType[layerType] || []
    originalByType[layerType].push(layer)
  }

  const newByType: { [key: string]: Layer[] } = {}
  for (const layer of newLayers) {
    const layerType = layer.sub_type
    newByType[layerType] = newByType[layerType] || []
    newByType[layerType].push(layer)
  }

  const validations: Validation[] = []

  const allTypes = new Set([...Object.keys(originalByType), ...Object.keys(newByType)])
  for (const layerType of allTypes) {
    if (layerType === 'background') continue

    const originalLayers = originalByType[layerType] || []
    const newTypeLayers = newByType[layerType] || []

    if (originalLayers.length === 0) {
      if (layerType !== 'product') {
        validations.push({
          name: `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} Position`,
          status: 'fail',
          details: `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} is present in new design but was not in the original.`,
        })
      }
      continue
    }
    if (newTypeLayers.length === 0) {
      validations.push({
        name: `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} Position`,
        status: 'fail',
        details: `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} is missing in the new design.`,
      })
      continue
    }

    if (layerType === 'product') {
      let productsOutsideBounds = 0
      let productsPartiallyOutside = 0
      const origLeft = VALID_PLACEMENT_AREA.left
      const origTop = VALID_PLACEMENT_AREA.top

      let maxPercentageOutOfBounds = 0
      let validatePercentOutOfBounds = false
      for (const newProduct of newTypeLayers) {
        const { top: newTop, left: newLeft } = adjustPositionForOrigin(
          newProduct.position.top,
          newProduct.position.left,
          newProduct.position.height,
          newProduct.position.width,
          newProduct.originY,
          newProduct.originX,
          newProduct.scaleX,
          newProduct.scaleY,
        )

        const leftDiff = origLeft - newLeft
        const topDiff = origTop - newTop

        // Area-based validation (existing logic)
        if (leftDiff > 0 && topDiff > 0) {
          const maxDiff = Math.max(leftDiff, topDiff)
          if (maxDiff > largeBuffer) {
            productsOutsideBounds++
          } else if (maxDiff > 0) {
            productsPartiallyOutside++
          }
        } else if (leftDiff < 0 || topDiff < 0) {
          const maxDiff = Math.max(leftDiff, topDiff)
          if (maxDiff > largeBuffer) {
            productsOutsideBounds++
            continue
          } else if (maxDiff > 0) {
            productsPartiallyOutside++
            continue
          }
          const percent = validateLayerOutOfBounds(newProduct, canvasWidth, canvasHeight)
          maxPercentageOutOfBounds = Math.max(maxPercentageOutOfBounds, percent)
          validatePercentOutOfBounds = true
        }
      }

      // Percentage-based validation
      if (validatePercentOutOfBounds && maxPercentageOutOfBounds > 0) {
        let maxBuffer = 25
        let minBuffer = 20
        let horizontalProducts = 0
        const placementStore = usePlacementStore()
        if (placementStore.selectedPlacementOption === PLACEMENT_TYPES.CORNER) {
          const processedProducts = placementStore.processedProductData
          if (processedProducts.length == 3) {
            for (const newProduct of processedProducts) {
              const aspectType = classifyAspectRatio(
                newProduct.fabricImage.width * newProduct.fabricImage.scaleX,
                newProduct.fabricImage.height * newProduct.fabricImage.scaleY,
              )
              if (aspectType == PRODUCT_ASPECT_TYPES.HORIZONTAL) {
                horizontalProducts++
              }
            }
            if (horizontalProducts == 3) {
              maxBuffer = 50
              minBuffer = 40
            }
          }
        }
        if (maxPercentageOutOfBounds > maxBuffer) {
          productsOutsideBounds++
        } else if (maxPercentageOutOfBounds > minBuffer) {
          productsPartiallyOutside++
        }
      }

      let status: 'pass' | 'fail' | 'warn'
      let detail: string

      if (productsOutsideBounds > 0) {
        status = 'fail'
        detail = `Product(s) positioned significantly outside original product boundaries`
      } else if (productsPartiallyOutside > 0) {
        status = 'warn'
        detail = `Product(s) positioned slightly outside original product boundaries`
      } else {
        status = 'pass'
        detail = 'All products are positioned within acceptable boundaries'
      }

      validations.push({
        name: 'Product Position',
        status,
        details: detail,
      })
    } else if (layerType === 'headline' || layerType === 'subheadline') {
      const validRegion = {
        headline: {
          1: { minTop: 265, maxTop: 380, left: 69 },
          2: { minTop: 345, maxTop: 380, left: 69 },
        },
        subheadline: { minTop: 330, maxTop: 400, left: 69 },
      }
      const newLayer = newTypeLayers[0]
      const newTop = newLayer.position.top
      const newLeft = newLayer.position.left
      //check newtop is in valid region
      let minTop = 0
      let maxTop = 0
      let left = 0
      let status: 'pass' | 'fail' | 'warn' = 'pass'
      let detail: string = ''
      if (layerType === 'headline') {
        const creativeStore = useCreativeStore()
        const canvasStore = useCanvasStore()
        const canvas = canvasStore.fabricCanvas || creativeStore.selectedCreative?.canvas
        const textLines: 1 | 2 =
          (canvas.getObjects().find((obj: any) => obj.layerName === 'Headline')?._textLines
            .length as 1 | 2) || 1
        maxTop = validRegion.headline[textLines].maxTop
        minTop = validRegion.headline[textLines].minTop
        left = validRegion.headline[textLines].left
      } else if (layerType === 'subheadline') {
        minTop = validRegion.subheadline.minTop
        maxTop = validRegion.subheadline.maxTop
        left = validRegion.subheadline.left
      }
      if (newTop < minTop || newTop > maxTop || newLeft != left) {
        if (
          (newTop < minTop && newTop >= minTop - 10) ||
          (newTop <= maxTop + 10 && newTop > maxTop) ||
          (newLeft < left && newLeft >= left - 10) ||
          (newLeft <= left + 10 && newLeft >= left)
        ) {
          status = 'warn'
          detail = `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} position has slight deviation`
        } else if (
          newTop < minTop - 10 ||
          newTop > maxTop + 10 ||
          newLeft < left - 10 ||
          newLeft > left + 10
        ) {
          status = 'fail'
          detail = `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} position has significant deviation`
        }
      } else {
        status = 'pass'
        detail = `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} is positioned correctly`
      }
      validations.push({
        name: `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} Position`,
        status,
        details: detail,
      })
    } else {
      // For all other types, use rotated center for position comparison
      const originalLayer = originalLayers[0]
      const newLayer = newTypeLayers[0]
      const origCenter = getRotatedCenter(originalLayer)
      const newCenter = getRotatedCenter(newLayer)
      let status: 'pass' | 'fail' | 'warn'
      let detail: string
      if (layerType === 'logo' || layerType === 'Logo - Horizontal') {
        const distance = Math.sqrt(
          Math.pow(origCenter.x - newCenter.x, 2) + Math.pow(origCenter.y - newCenter.y, 2),
        )
        if (distance <= smallBuffer) {
          status = 'pass'
          detail = 'Logo is positioned correctly'
        } else if (distance <= largeBuffer) {
          status = 'warn'
          detail = `Logo position has slight deviation (${Math.round(distance)}px)`
        } else {
          status = 'fail'
          detail = `Logo position has significant deviation (${Math.round(distance)}px)`
        }
      } else {
        let topDiff = 0
        let leftDiff = 0
        const isNotValid = false
        topDiff = Math.abs(origCenter.y - newCenter.y)
        leftDiff = Math.abs(origCenter.x - newCenter.x)
        const maxDiff = Math.max(topDiff, leftDiff)
        if (maxDiff <= smallBuffer) {
          status = 'pass'
          detail = `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} is positioned correctly`
        } else if (maxDiff <= largeBuffer) {
          status = 'warn'
          detail = `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} position has slight deviation`
        } else {
          status = 'fail'
          detail = `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} position has significant deviation`
        }
      }
      validations.push({
        name: `${layerType.charAt(0).toUpperCase() + layerType.slice(1)} Position`,
        status,
        details: detail,
      })
    }
  }

  return validations
}

// Helper: Project polygon onto axis
function projectPolygon(axis: { x: number; y: number }, points: { x: number; y: number }[]) {
  let min = Infinity,
    max = -Infinity
  for (const p of points) {
    const projection = p.x * axis.x + p.y * axis.y
    if (projection < min) min = projection
    if (projection > max) max = projection
  }
  return { min, max }
}

// Helper: Get edge normals from corners
function getEdgeNormals(corners: { x: number; y: number }[]) {
  const normals = []
  for (let i = 0; i < corners.length; i++) {
    const p1 = corners[i]
    const p2 = corners[(i + 1) % corners.length]
    const edge = { x: p2.x - p1.x, y: p2.y - p1.y }
    // Perpendicular (normal)
    normals.push({ x: -edge.y, y: edge.x })
  }
  return normals
}

// OBB (SAT) overlap check
function obbOverlap(cornersA: { x: number; y: number }[], cornersB: { x: number; y: number }[]) {
  const axes = [...getEdgeNormals(cornersA), ...getEdgeNormals(cornersB)]
  for (const axis of axes) {
    // Normalize axis
    const length = Math.sqrt(axis.x * axis.x + axis.y * axis.y)
    const norm = { x: axis.x / length, y: axis.y / length }
    const projA = projectPolygon(norm, cornersA)
    const projB = projectPolygon(norm, cornersB)
    if (projA.max < projB.min || projB.max < projA.min) {
      return false // Separating axis found
    }
  }
  return true // No separating axis, overlap exists
}

function isVisuallyOverlapping(
  obj1: FabricObject,
  obj2: FabricObject,
  canvas: Canvas,
  overlapBounds: {
    overlapMinX: number
    overlapMaxX: number
    overlapMinY: number
    overlapMaxY: number
  },
): boolean {
  const { overlapMinX, overlapMaxX, overlapMinY, overlapMaxY } = overlapBounds
  const sampleStep = 5
  const zoom = canvas.getZoom()
  const canvasWidth = canvas.getWidth()
  const canvasHeight = canvas.getHeight()

  const offscreen1 = document.createElement('canvas')
  const offscreen2 = document.createElement('canvas')
  offscreen1.width = canvasWidth
  offscreen1.height = canvasHeight
  offscreen2.width = canvasWidth
  offscreen2.height = canvasHeight

  const ctx1 = offscreen1.getContext('2d', { willReadFrequently: true })!
  const ctx2 = offscreen2.getContext('2d', { willReadFrequently: true })!

  ctx1.setTransform(zoom, 0, 0, zoom, 0, 0)
  ctx2.setTransform(zoom, 0, 0, zoom, 0, 0)

  ctx1.clearRect(0, 0, canvasWidth, canvasHeight)
  ctx2.clearRect(0, 0, canvasWidth, canvasHeight)
  obj1.render(ctx1)
  obj2.render(ctx2)

  for (let x = Math.floor(overlapMinX); x < Math.ceil(overlapMaxX); x += sampleStep) {
    for (let y = Math.floor(overlapMinY); y < Math.ceil(overlapMaxY); y += sampleStep) {
      const screenX = Math.round(x * zoom)
      const screenY = Math.round(y * zoom)

      const pixel1 = ctx1.getImageData(screenX, screenY, 1, 1).data
      const pixel2 = ctx2.getImageData(screenX, screenY, 1, 1).data

      const isOpaque1 = pixel1[3] > 0
      const isOpaque2 = pixel2[3] > 0

      if (isOpaque1 && isOpaque2) {
        return true
      }
    }
  }

  return false
}

const validateLayerOverlaps = (layers: Layer[]): Validation[] => {
  const MAJOR_OVERLAP_THRESHOLD = 15
  const nonBgLayers = layers.filter((layer) => layer.sub_type !== 'background')
  const significantOverlaps: { elements: string; percentage: number }[] = []
  const minorOverlaps: { elements: string; percentage: number }[] = []
  const overlapPairs = new Set<string>()

  function getAABB(corners: { x: number; y: number }[]) {
    const xs = corners.map((c) => c.x)
    const ys = corners.map((c) => c.y)
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    }
  }

  for (let i = 0; i < nonBgLayers.length; i++) {
    for (let j = i + 1; j < nonBgLayers.length; j++) {
      const layer1 = nonBgLayers[i]
      const layer2 = nonBgLayers[j]
      if (layer1.sub_type === 'product' && layer2.sub_type === 'product') {
        continue
      }
      const pairKey = `${Math.min(i, j)}-${Math.max(i, j)}`
      if (overlapPairs.has(pairKey)) {
        continue
      }
      overlapPairs.add(pairKey)
      const poly1 = getRotatedCorners(layer1)
      const poly2 = getRotatedCorners(layer2)
      // Use OBB (SAT) for overlap detection
      if (obbOverlap(poly1, poly2)) {
        // Use AABB intersection area for severity
        const aabb1 = getAABB(poly1)
        const aabb2 = getAABB(poly2)
        const overlapMinX = Math.max(aabb1.minX, aabb2.minX)
        const overlapMaxX = Math.min(aabb1.maxX, aabb2.maxX)
        const overlapMinY = Math.max(aabb1.minY, aabb2.minY)
        const overlapMaxY = Math.min(aabb1.maxY, aabb2.maxY)
        const overlapWidth = overlapMaxX - overlapMinX
        const overlapHeight = overlapMaxY - overlapMinY
        let overlapArea = 0
        if (overlapWidth > 0 && overlapHeight > 0) {
          overlapArea = overlapWidth * overlapHeight

          const area1 = (aabb1.maxX - aabb1.minX) * (aabb1.maxY - aabb1.minY)
          const area2 = (aabb2.maxX - aabb2.minX) * (aabb2.maxY - aabb2.minY)
          const percent1 = area1 > 0 ? (overlapArea / area1) * 100 : 0
          const percent2 = area2 > 0 ? (overlapArea / area2) * 100 : 0
          const overlapPercentage = Math.max(percent1, percent2)
          if (overlapPercentage > 0) {
            // Check if visually overlapping
            const creativeStore = useCreativeStore()
            const canvasStore = useCanvasStore()
            const fabricCanvas = canvasStore.fabricCanvas || creativeStore.selectedCreative?.canvas
            const fabricObj1 = fabricCanvas
              .getObjects()
              .find(
                (obj: any) =>
                  obj.layerName ===
                  (/^Product \d+$/.test(layer1.name) ? layer1.name.replace(' ', '') : layer1.name),
              )

            const fabricObj2 = fabricCanvas
              .getObjects()
              .find(
                (obj: any) =>
                  obj.layerName ===
                  (/^Product \d+$/.test(layer2.name) ? layer2.name.replace(' ', '') : layer2.name),
              )

            if (!fabricObj1 || !fabricObj2) continue

            const visuallyOverlapping = isVisuallyOverlapping(
              fabricObj1,
              fabricObj2,
              fabricCanvas,
              { overlapMinX, overlapMaxX, overlapMinY, overlapMaxY },
            )

            if (!visuallyOverlapping) continue // Skip this pair
          }

          let layer1Name;
          let layer2Name;
          if (layer1.name === "CTA - Order now") {
            layer1Name = "CTA"
          } else if (layer1.name === "Logo - Horizontal") {
            layer1Name = "Logo"
          } else {
            layer1Name = layer1.name || layer1.sub_type
          }

          if (layer2.name === "CTA - Order now") {
            layer2Name = "CTA"
          } else if (layer2.name === "Logo - Horizontal") {
            layer2Name = "Logo"
          } else {
            layer2Name = layer2.name || layer2.sub_type
          }

          const overlapInfo = {
            elements: `${layer1Name} and ${layer2Name}`,
            percentage: Math.round(overlapPercentage * 10) / 10,
          }
          if (overlapPercentage > MAJOR_OVERLAP_THRESHOLD) {
            significantOverlaps.push(overlapInfo)
          } else if (overlapPercentage > 0) {
            minorOverlaps.push(overlapInfo)
          }
        }
      }
    }
  }
  const validations: Validation[] = []
  if (significantOverlaps.length > 0) {
    significantOverlaps.sort((a, b) => b.percentage - a.percentage)
    let details = `Found ${significantOverlaps.length} significant element overlaps:`
    significantOverlaps.forEach((overlap, index) => {
      details += `\n${index + 1}. ${overlap.elements}`
    })
    validations.push({
      name: 'Element Overlaps',
      status: 'fail',
      details,
    })
  }
  if (minorOverlaps.length > 0) {
    minorOverlaps.sort((a, b) => b.percentage - a.percentage)
    let details = `Found ${minorOverlaps.length} minor element overlaps:`
    minorOverlaps.forEach((overlap, index) => {
      details += `\n${index + 1}. ${overlap.elements}`
    })
    validations.push({
      name: 'Element Overlaps',
      status: 'warn',
      details,
    })
  }
  return validations
}

// Area-based out-of-bounds check for all layers
const validateLayerOutOfBounds = (
  layer: Layer,
  canvasWidth: number,
  canvasHeight: number,
): number => {
  if (layer.sub_type === 'background') return 0
  const corners = getRotatedCorners(layer)
  const clippedPolygon = clipPolygonToCanvas(corners, canvasWidth, canvasHeight)
  let visibleArea = 0
  if (clippedPolygon.length >= 3) {
    visibleArea = polygonArea(clippedPolygon)
  }
  const totalArea = polygonArea(corners)
  let percentOutside = 0
  if (totalArea > 0) {
    percentOutside = ((totalArea - visibleArea) / totalArea) * 100
    percentOutside = Math.max(0, percentOutside)
  }
  return percentOutside
}

const buttonValidation = (
  text: string,
): { success: boolean; validations: Validation[]; message: string } => {
  const foodCategories = [
    'Cereals and Breakfast',
    'Cold Drinks and Juices',
    'Munchies And Snacks',
    'Instant Food and Frozen Food',
    'Tea, Coffee and More',
    'Chocolates',
    'Biscuits and Cakes',
    'Sauces and Spreads',
    'Dairy, Bread and Eggs',
    'Edible Oils and Ghee',
    'Masala, Pickle and Papad',
    'Atta, Rice and Dals',
    'Drinks',
    'Chips',
    'Baby',
    'Pasta & Noodles',
    'Specialty Fruit',
    'Frozen Foods',
  ]
  const productStore = useProductStore()
  const selectedProducts = productStore.baseProducts
  const foodProducts = selectedProducts.filter((product) =>
    foodCategories.includes(product.parentCategory),
  )
  const editorStore = useEditorStore()
  const appStore = useAppStore()
  if (appStore.selectedDesignFlow.value != designFlows.upload.value && appStore.selectedDesignFlow.value != designFlows.convert.value) {
    if (foodProducts.length === selectedProducts.length && !text.toLowerCase().includes('order')) {
      return {
        success: false,
        validations: [
          {
            name: 'Button Text',
            status: 'fail',
            details: "CTA must be 'Order now' for food and drink products.",
          },
        ],
        message: 'Button text validation failed for food products.',
      }
    } else if (foodProducts.length === 0 && !text.toLowerCase().includes('buy')) {
      return {
        success: false,
        validations: [
          {
            name: 'Button Text',
            status: 'fail',
            details: "CTA must be 'Buy now' for non-food products.",
          },
        ],
        message: 'Button text validation failed for non-food products.',
      }
    } else if (
      foodProducts.length !== selectedProducts.length &&
      !text.toLowerCase().includes('buy')
    ) {
      return {
        success: false,
        validations: [
          {
            name: 'Button Text',
            status: 'fail',
            details: "CTA must be 'Buy now' when products include both food and non-food items.",
          },
        ],
        message: 'Button text validation failed for non-food products.',
      }
    }
  }
  return {
    success: true,
    validations: [
      {
        name: 'Button Text',
        status: 'pass',
        details: 'Button text is valid.',
      },
    ],
    message: 'Button text validation passed.',
  }
}

export const validateWithCanvasJson = (
  canvas: any,
  guidelines: CreativeSetGuidelines,
  newCanvas: any,
): { success: boolean; validations: Validation[]; message: string } => {
  try {
    const adSize = guidelines.ad_size
    const layerMapping: LayerMapping = {
      Logo: { type: 'image', sub_type: 'logo', is_product: false },
      'Logo - Horizontal': { type: 'image', sub_type: 'logo', is_product: false },
      Product1: { type: 'image', sub_type: 'product', is_product: true },
      Headline: { type: 'text', sub_type: 'headline', is_product: false },
      SubHeadline: { type: 'text', sub_type: 'subheadline', is_product: false },
      CTA: { type: 'button', sub_type: 'CTA', is_product: false },
      'CTA - Order now': { type: 'button', sub_type: 'CTA', is_product: false },
      BG: { type: 'image', sub_type: 'background', is_product: false },
    }

    const newLayerMapping = { ...layerMapping }
    for (const obj of newCanvas.objects) {
      const layerName = obj.layerName || ''
      if (['Product2', 'Product3'].includes(layerName)) {
        newLayerMapping[layerName] = { type: 'image', sub_type: 'product', is_product: true }
      }
    }
    const canvasLayers = convertCanvasToLayers(canvas, adSize, 'Red Bull', layerMapping)
    const newCanvasLayers = convertCanvasToLayers(newCanvas, adSize, 'Red Bull', newLayerMapping)
    // Remove invisible layers from both canvasLayers and newCanvasLayers
    const invisibleLayers = newCanvasLayers.objects
      .filter((layer) => layer.visible === false)
      .map((layer) => layer.name)
    canvasLayers.objects = canvasLayers.objects.filter(
      (layer) => !invisibleLayers.includes(layer.name),
    )
    newCanvasLayers.objects = newCanvasLayers.objects.filter(
      (layer) => !invisibleLayers.includes(layer.name),
    )

    // Adjust VALID_PLACEMENT_AREA based on invisible layers using config values
    const adSizeConfigStore = useAdSizeConfigStore()
    if (invisibleLayers.includes('CTA')) {
      VALID_PLACEMENT_AREA.left = adSizeConfigStore.getValidPlacementArea(true).left
    } else if (invisibleLayers.includes('CTA - Order now')) {
      VALID_PLACEMENT_AREA.left = adSizeConfigStore.getValidPlacementArea(false).left
    }

    const positionValidations = validatePositionsWithCanvasJson(
      canvasLayers.objects,
      newCanvasLayers.objects,
      adSize,
    )

    // Polygon-based overlap validation
    const overlapValidations = validateLayerOverlaps(newCanvasLayers.objects)

    // Button validation
    const buttonLayer = newCanvasLayers.objects.find((layer) => layer.sub_type === 'CTA')
    let buttonValidations: Validation[] = []
    if (buttonLayer) {
      const buttonText = buttonLayer.value || ''
      const buttonValidationResult = buttonValidation(buttonText)
      buttonValidations = buttonValidationResult.validations
    }
    return {
      success: true,
      validations: [...positionValidations, ...overlapValidations, ...buttonValidations],
      message: 'Ad validated successfully',
    }
  } catch (e) {
    console.error(`Error in validateWithCanvasJson: ${e}`)
    throw e
  }
}
